<template>
  <aside
    v-if="showTake5DailySidebar"
    aria-label="Take 5 Daily Sidebar Content"
    class="py-[2.143rem] lg:py-12 px-0 border-l-0 md:border-l md:border-gray-300 bg-gray-200 z-10"
  >
    <section class="border-b border-gray-200 lg:p-12 lg:pr-0 md:p-12 md:pr-0 px-6 py-5">
      <div class="mb-6 border-b border-gray-300 pb-[1.0714rem]">
        <Heading :level="4" size="xl" color="text-ash-200">What's Trending</Heading>
      </div>
      <ul class="space-y-3">
        <li
          v-for="(item, index) in trending"
          :key="index"
          class="flex items-start space-x-2 -mx-3 -mt-3 mb-3"
        >
        <span class="w-[2.5rem] mr-0">
          <Icon :icon="iconFor(item.entityType)" class="text-green-300 mt-1 p-3 mr-0" /></span>
          <NuxtLink 
            :aria-label="item.title" 
            :title="item.title" 
            :id="`trending-link-${index}`" 
            :to="item.href" 
            class="hover:text-gold-100 text-ash-100 py-3 pr-3" 
            external
          >
            {{ item.title }}
          </NuxtLink>
        </li>
      </ul>
    </section>

    <section class="border-b border-gray-200 lg:p-12 lg:pr-0 md:p-12 md:pr-0 px-6 py-5" v-if="showListenElsewhere">
      <div class="mb-6 border-b border-gray-300 pb-[1.0714rem] !text-ash-200">
        <Heading :level="4" size="xl" color="text-ash-200">Listen Elsewhere</Heading>
      </div>
        <ul class="space-y-3">
          <li
              v-for="(item, index) in listen"
              :key="index"
              class="flex items-center space-x-3"
          >
              <Icon
              v-if="item.icon"
              :icon="item.icon"
              packPrefix="fab"
              :class="[
                  item.title === 'Spotify' ? 'text-green-300 w-[1rem]' : '',
                  item.title === 'Apple' ? 'apple-icon dark:text-gray-100 fill-current w-[1rem] h-[1rem]' : ''
              ]"
              />
                <NuxtLink 
                  aria-label="Listen elsewhere link" 
                  title="Listen elsewhere link" 
                  :id="`listen-link-${index}`" 
                  :to="item.href" 
                  class="flex items-center space-x-1 hover:text-gold-100 text-ash-100" 
                  external
                >
                  <span>{{ item.title }}</span>
                  <Icon
                      icon="external-link-square-alt"
                      packPrefix="far"
                  />
              </NuxtLink>
          </li>
        </ul>
    </section>

    <section class="lg:p-12 lg:pr-0 md:p-12 md:pr-0 px-6 py-5"> 
      <div class="leading-[1.125] mb-[1.5rem] border-b border-gray-300 pb-[1.0714285714rem]">
        <Heading :level="4" size="xl" color="text-ash-200">Get a Monthly Dose</Heading>
      </div>
      <EmailSubscriptionForm
        id="t5d-form"
        context="take5DailySidebar"
        list-names="take-5-daily-opt-ins"
        @on-success="toastStore.publish('Thank you for subscribing!', 'success')"
        @on-error="toastStore.publish('Unable to submit email', 'error')"
      />
      <p class="text-ash-100 text-md leading-[1.710rem] font-normal mt-[1.4285714286rem] mb-[1rem]">
        News, videos, and Thorne stories delivered to your inbox each month.
      </p>
    </section>
  </aside>

  <aside
    v-if="showForFaQSidebar"
    aria-label="FAQ Sidebar Content"
    class="px-0 border-l-0 md:border-l md:border-gray-300 bg-gray-200 z-10"
  >
    <section class="border-b border-gray-200 lg:p-12 lg:pr-0 md:p-12 md:pr-0 px-6 py-5" v-if="showFaqSearchTips">
      <div class="mb-4 border-b border-gray-300 pb-[0.625rem] !text-ash-200">
       <Heading :level="3" size="xl" color="text-ash-200" :icon="'comment-exclamation'" iconClass="text-pink w-6 !h-6">
          Search Tips
        </Heading>
      </div>
      <p class="text-ash-100 text-md font-normal mb-[1rem]">
        If you're coming up empty, try related search terms, or browse
        using the categories on the
        <NuxtLink 
          aria-label="Main support page" 
          title="Main support page" 
          id="main-support-page"
          class="underline"
          :to="URL_CONFIG.FAQS"
          external
        >
          main support page
        </NuxtLink>
      </p>
    </section>

    <section class="border-b border-gray-200 pt-[2.143rem] lg:pt-[3.571rem] pl-[1.429rem] pr-[1.429rem] lg:pl-12 lg:pr-0" v-if="showFaqHelpful">
      <div class="border-b border-gray-300 pb-[1.643rem] mb-6 !text-ash-200">
        <Heading :level="3" color="text-ash-200">Answers others found helpful</Heading>
      </div>
      <ul class="space-y-3">
        <li
          v-for="(item, index) in faqHelpful"
          :key="index"
          class="flex items-start mb-4"
        >
          <NuxtLink 
            :to="item.data.faqUrlKey" 
            class="hover:text-gold-100 text-ash-500 font-normal cursor-pointer" 
            :aria-label="item.data.faqQuestion" 
            :title="item.data.faqQuestion" 
            :id="`faq-ques-${index}`"
            external
          >
            {{ item.data.faqQuestion }}
          </NuxtLink>
        </li>
      </ul>
    </section>

    <section class="pl-[1.571rem] lg:pl-12 pr-[1.571rem] lg:pr-0 my-[3.714rem] pb-[2.143rem] lg:pb-0"> 
      <div class="border-b border-gray-300 pb-[1.643rem] mb-6 !text-ash-200">
        <Heading :level="3" color="text-ash-200">Not finding your answer?</Heading>
      </div>
      <p class="text-ash-100 font-normal"><NuxtLink :to="URL_CONFIG.CONTACTS" class="underline" aria-label="chat with us" title="chat with us" id="chat-with-us" external>Chat with us</NuxtLink>
        or call 1-800-228-1966
      </p>
    </section>
  </aside>

  <aside
    v-if="showForNewsroomSidebar"
    aria-label="Newsroom Sidebar Content"
    class="py-[2.143rem] lg:py-12 px-0 border-l-0 md:border-l md:border-gray-300 bg-gray-200 z-10"
  >
    <section class="p-12">
      <div class="border-b border-gray-200 mb-[4rem]" v-if="multiMedia?.length">
        <div class="mb-[1.214rem] pb-[1.214rem] border-b border-gray-300">
          <Heading :level="4" size="xl" :icon="'broadcast-tower'" packPrefix="fal" iconClass="text-[1.286rem]  mr-[0.714rem] text-ash-100" color="text-ash-200">Multimedia</Heading>
        </div>
        <ul class="space-y-3">
          <li
            v-for="(item, index) in multiMedia"
            :key="index"
            class="flex space-x-2 newsroom-side-section items-center mb-[1.071rem]"
          >
            <Icon v-if="item.packPrefix && item.icon" :icon="item.icon" :packPrefix="item.packPrefix" class="h-5 w-5 text-ash-200" />
            <Icon v-else-if="item.icon" :icon="item.icon" class="h-5 w-5 text-ash-200" />
            <NuxtLink 
              :to="item.href" 
              class="hover:text-gold-100 text-ash-100"
              :title="item.title"
              :aria-label="item.title"
              :id="`multi-media-${index}`"
              target="_blank"
              external
            >
              {{ item.title }}
            </NuxtLink>
          </li>
        </ul>
      </div>

      <div class="mb-[4rem]"> 
        <div class="mb-[1.214rem] pb-[1.214rem] border-b border-gray-300 ">
          <Heading :level="4" size="xl" :icon="'paper-plane'" iconClass="text-ash-100 mr-[0.714rem] text-[1.286rem]" color="text-ash-200">Inquiries</Heading>
        </div>
        <p class="text-ash-100">For press inquiries, please email us at 
          <NuxtLink 
            external
            href="mailto:media@thorne.com"
            title="Email media@thorne.com"
            aria-label="Email media@thorne.com"
            id="send-media-mail"
          >
            media@thorne.com
          </NuxtLink>
        </p>
      </div>
  </section>
  </aside>
</template>

<script setup lang="ts">
import Heading from '../../atom/heading.vue'
import Icon from '../../atom/icon.vue'
import EmailSubscriptionForm from '../../organism/emailSubscriptionForm.vue'
import {useToastStore} from '~/stores/toastStore'
import { URL_CONFIG } from '~~/shared/utils/url.const'

const toastStore = useToastStore();

interface LinkItem {
  title: string;
  href?: string;
  icon?: string;
  packPrefix?: string;
  entityType?: string;
}

interface FaqEntry {
  id: string;
  data: {
    faqUrlKey: string;
    faqQuestion: string;
    faqAnswer?: string;
    faqUpvotes?: number;
  };
}

const props = defineProps<{
  showTake5DailySidebar?: boolean;
  showListenElsewhere?: boolean;
  showForFaQSidebar?: boolean;
  showFaqSearchTips?: boolean;
  showFaqHelpful?: boolean;
  showForNewsroomSidebar?: boolean;
  showOnlyNewsroomPage?: boolean;
  showOnlyNewsroomDetails?: boolean;
  trending?: LinkItem[];
  listen?: LinkItem[];
  faqHelpful?: FaqEntry[];
  onlyMedia?: LinkItem[];
  multiMedia?: LinkItem[];
}>()

// icon mapping helper
const iconFor = (entityType) => {
  switch ((entityType || '').toLowerCase()) {
    case 'podcasts':
      return 'podcast';
    case 'videos':
      return 'video';
    case 'articles':
    default:
      return 'newspaper';
  }
};
</script>
 